// GBP post input — the ONE validation shape shared by the compose form and
// POST /api/gbp/:siteId/posts, so a too-long summary or a missing event date
// is caught in the form and again at the route with identical rules, never
// discovered by a wasted Google call (design §5).
//
// Google's own hard limits encoded here: summary ≤ 1500 chars, event title
// ≤ 58 chars. An OFFER's validity window rides in the same `event` object
// Google uses for events — that is Google's shape, not ours.
//
// Leaf module (zod only) → node --test runs it directly.

import { z } from 'zod';

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

export const GbpPostCtaSchema = z
  .object({
    actionType: z.enum(['LEARN_MORE', 'BOOK', 'ORDER', 'SHOP', 'SIGN_UP', 'CALL']),
    url: z.string().url().max(2000).optional(),
  })
  .refine((cta) => cta.actionType === 'CALL' || typeof cta.url === 'string', {
    message: 'This button type needs a link.',
    path: ['url'],
  });

export const GbpPostDateSchema = z.object({
  date: z.string().regex(DATE_RE, 'Use the YYYY-MM-DD format.'),
  time: z.string().regex(TIME_RE, 'Use the HH:MM format.').optional(),
});

export const GbpPostWindowSchema = z
  .object({
    title: z.string().trim().min(1).max(58, 'Google limits this title to 58 characters.'),
    start: GbpPostDateSchema,
    end: GbpPostDateSchema,
  })
  .refine(
    (w) =>
      w.start.date < w.end.date ||
      (w.start.date === w.end.date &&
        (!w.start.time || !w.end.time || w.start.time <= w.end.time)),
    {
      message: 'The event ends before it starts.',
      path: ['end'],
    },
  );

export const GbpPostOfferSchema = z.object({
  couponCode: z.string().trim().min(1).max(58).optional(),
  redeemUrl: z.string().url().max(2000).optional(),
  terms: z.string().trim().min(1).max(5000).optional(),
});

// ── S4: scheduling ──────────────────────────────────────────────────────────
// The wire carries epoch milliseconds (client computes it from the owner's
// LOCAL date+time inputs — epoch is timezone-unambiguous; a "2026-08-15
// 09:00" string would silently mean a different moment on the server).
// Whether that moment is schedulable is a NOW-relative judgment, so it lives
// in scheduleProblem (shared by form and route), not in the schema.

export const GbpPostRecurrenceInputSchema = z.object({
  cadence: z.enum(['weekly', 'monthly']),
  until: z.string().regex(DATE_RE, 'Use the YYYY-MM-DD format.').optional(),
});

// FIX 4 (S4 review, Important): REQUIRED, not optional/defaulted — the
// client always knows it (`new Date().getTimezoneOffset()` at schedule
// time), and a missing value would silently fall back to "the owner is in
// UTC," which is exactly the bug this fix closes (monthly recurrence
// anchoring on the SERVER's UTC day instead of the owner's local day for a
// US evening pick). Same sign convention as getTimezoneOffset(): UTC =
// local + tzOffsetMinutes*60000, so PDT (UTC-7) is +420, not -420. Range
// covers every real UTC offset (UTC-14 to UTC+14) with margin.
export const GbpPostScheduleSchema = z.object({
  publishAtMs: z.number().int().positive(),
  tzOffsetMinutes: z.number().int().min(-840).max(840),
  recurrence: GbpPostRecurrenceInputSchema.optional(),
});

/** What gbp_posts.recurrence_json stores (Zod boundary on read AND write —
 *  CLAUDE.md JSON-column rule; the cron reads through this). `startedOn`
 *  (first occurrence's date, in the OWNER'S LOCAL calendar as of FIX 4)
 *  anchors the 1-year default cap; `dayOfMonth` anchors monthly clamping so
 *  Jan 31 → Feb 28 → Mar 31, not a drifting 28 — also owner-local as of
 *  FIX 4. `tzOffsetMinutes` defaults to 0 so pre-FIX-4 stored rows (which
 *  predate this field) keep parsing and keep their old UTC-anchored
 *  behavior byte-for-byte — this is a backward-compatibility default, not a
 *  claim that a UTC owner is the common case. */
export const GbpPostRecurrenceStoredSchema = z.object({
  cadence: z.enum(['weekly', 'monthly']),
  until: z.string().regex(DATE_RE).optional(),
  startedOn: z.string().regex(DATE_RE),
  dayOfMonth: z.number().int().min(1).max(31).optional(),
  tzOffsetMinutes: z.number().int().min(-840).max(840).default(0),
});
export type GbpPostRecurrenceStored = z.infer<typeof GbpPostRecurrenceStoredSchema>;

/** 2 min: a slow form submit must not bounce as "in the past". */
export const SCHEDULE_PAST_TOLERANCE_MS = 2 * 60 * 1000;
/** 366 days: past the recurrence cap there is nothing honest to promise. */
export const SCHEDULE_MAX_AHEAD_MS = 366 * 24 * 60 * 60 * 1000;

export function scheduleProblem(publishAtMs: number, nowMs: number): 'past' | 'too_far' | null {
  if (!Number.isFinite(publishAtMs)) return 'past';
  if (publishAtMs < nowMs - SCHEDULE_PAST_TOLERANCE_MS) return 'past';
  if (publishAtMs > nowMs + SCHEDULE_MAX_AHEAD_MS) return 'too_far';
  return null;
}

export const GbpPostInputSchema = z
  .object({
    type: z.enum(['update', 'offer', 'event']),
    summary: z.string().trim().min(1).max(1500, 'Google limits posts to 1,500 characters.'),
    cta: GbpPostCtaSchema.optional(),
    event: GbpPostWindowSchema.optional(),
    offer: GbpPostOfferSchema.optional(),
    schedule: GbpPostScheduleSchema.optional(),
  })
  .superRefine((post, ctx) => {
    if ((post.type === 'event' || post.type === 'offer') && !post.event) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['event'],
        message:
          post.type === 'event'
            ? 'An event needs a title and start/end dates.'
            : 'An offer needs a title and a validity window.',
      });
    }
    if (post.type !== 'offer' && post.offer) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['offer'],
        message: 'Coupon fields only apply to an offer post.',
      });
    }
    if (post.schedule?.recurrence && post.type !== 'update') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['schedule', 'recurrence'],
        message: 'Only a plain update can repeat — offers and events carry their own dates.',
      });
    }
  });

export type GbpPostInput = z.infer<typeof GbpPostInputSchema>;
/** What gbp_posts.payload_json stores — everything except type + summary,
 *  which have their own columns. */
export type GbpPostPayload = Pick<GbpPostInput, 'cta' | 'event' | 'offer'>;
